import { useState } from "react"
import { Link } from "@tanstack/react-router"
import { Button } from "@/components/ui/button"
// onAdd/onEdit drive the inline page flow owned by the route
import { Input } from "@/components/ui/input"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { useApiQuery } from "@/api/ApiQueryServiceProvider"
import { Search, Edit, Settings2, ChevronLeft, ChevronRight, Loader2, Plus } from "lucide-react"
import { Badge } from "@/components/ui/badge"

export interface ResourceType {
  id: string
  resourceTypeNumber: number
  resourceTypeName: string
  resourceTypeDescription: string
  resourceTypeCategory: string
  namespace: string
  parentResourceTypes: number[]
  permissions: string[]
}

type CategoryFilter = 'all' | 'artifact' | 'component' | 'product' | 'cloud' | 'user-data'

const CATEGORY_TABS: { value: CategoryFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'artifact', label: 'Artifact' },
  { value: 'component', label: 'Component' },
  { value: 'product', label: 'Product' },
  { value: 'cloud', label: 'Cloud' },
  { value: 'user-data', label: 'User Data' },
]

const CATEGORY_BADGE: Record<string, string> = {
  artifact: 'bg-blue-100 text-blue-800 hover:bg-blue-100',
  component: 'bg-green-100 text-green-800 hover:bg-green-100',
  product: 'bg-purple-100 text-purple-800 hover:bg-purple-100',
  cloud: 'bg-sky-100 text-sky-800 hover:bg-sky-100',
  'user-data': 'bg-orange-100 text-orange-800 hover:bg-orange-100',
}

export interface ResourceTypeListProps {
  onAdd?: () => void
  onEdit?: (resourceType: ResourceType) => void
}

export function ResourceTypeList({ onAdd, onEdit }: ResourceTypeListProps = {}) {
  const [searchQuery, setSearchQuery] = useState("")
  const [currentPage, setCurrentPage] = useState(1)
  const [selectedCategory, setSelectedCategory] = useState<CategoryFilter>('all')
  const itemsPerPage = 10

  const { data, isLoading, isError, refetch } = useApiQuery("getResourceTypes", {})

  const resourceTypes: ResourceType[] = ((data as any)?.content?.resourceTypes || (data as any)?.resourceTypes || []).map((rt: any) => ({
    id: rt.id || String(rt.resourceTypeNumber),
    resourceTypeNumber: rt.resourceTypeNumber,
    resourceTypeName: rt.resourceTypeName,
    resourceTypeDescription: rt.resourceTypeDescription ?? '',
    resourceTypeCategory: rt.resourceTypeCategory,
    namespace: rt.namespace ?? '',
    parentResourceTypes: rt.parentResourceTypes ?? [],
    permissions: rt.permissions ?? [],
  }))

  const filteredResourceTypes = resourceTypes.filter((rt) => {
    const matchesSearch = rt.resourceTypeName.toLowerCase().includes(searchQuery.toLowerCase())
    const matchesCategory = selectedCategory === 'all' || rt.resourceTypeCategory === selectedCategory
    return matchesSearch && matchesCategory
  })

  const totalPages = Math.ceil(filteredResourceTypes.length / itemsPerPage)
  const startIndex = (currentPage - 1) * itemsPerPage
  const paginatedResourceTypes = filteredResourceTypes.slice(startIndex, startIndex + itemsPerPage)

  // Resolve parent type names from the full list
  const getParentNames = (parentIds: number[]) => {
    return parentIds
      .map((id) => resourceTypes.find((rt) => rt.resourceTypeNumber === id)?.resourceTypeName ?? `#${id}`)
      .join(', ')
  }

  if (isLoading) {
    return (
      <Card className="shadow-sm">
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    )
  }

  if (isError) {
    return (
      <Card className="shadow-sm">
        <CardContent className="flex flex-col items-center justify-center py-12 gap-4">
          <p className="text-destructive">Failed to load resource types</p>
          <Button variant="outline" onClick={() => refetch()}>Try Again</Button>
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="space-y-4">
      <Card className="shadow-sm pb-0">
        <CardHeader className="border-b bg-slate-700 text-white [&_p]:text-slate-300">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
                <Settings2 className="h-5 w-5 text-primary" />
              </div>
              <div>
                <CardTitle className="text-xl">Manage Resource Types</CardTitle>
                <CardDescription className="text-base">Define resource types with path configurations</CardDescription>
              </div>
            </div>
            <Button size="sm" className="gap-2" onClick={() => onAdd?.()}>
              <Plus className="h-4 w-4" />
              Add Resource Type
            </Button>
          </div>
        </CardHeader>
      </Card>

      <Card className="shadow-sm pb-0">
        <CardContent className="p-4">
          <div className="flex flex-col gap-4">
            <div className="relative max-w-sm">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search resource types..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value)
                  setCurrentPage(1)
                }}
                className="pl-9"
              />
            </div>
            {/* Category filter tabs */}
            <div className="flex flex-wrap gap-2">
              {CATEGORY_TABS.map((tab) => (
                <Button
                  key={tab.value}
                  size="sm"
                  variant={selectedCategory === tab.value ? 'default' : 'outline'}
                  onClick={() => {
                    setSelectedCategory(tab.value)
                    setCurrentPage(1)
                  }}
                >
                  {tab.label}
                </Button>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="shadow-sm pb-0">
        <CardContent className="p-4">
          <Table className="text-base">
            <TableHeader>
              <TableRow className="bg-muted/30 hover:bg-muted/30">
                <TableHead className="w-[100px] font-semibold text-base">ID</TableHead>
                <TableHead className="font-semibold text-base">Name</TableHead>
                <TableHead className="font-semibold text-base">Description</TableHead>
                <TableHead className="w-[120px] font-semibold text-base">Category</TableHead>
                <TableHead className="font-semibold text-base">Parent Types</TableHead>
                <TableHead className="w-[100px] text-right font-semibold text-base">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {paginatedResourceTypes.length > 0 ? (
                paginatedResourceTypes.map((resourceType) => (
                  <TableRow key={resourceType.resourceTypeNumber} className="transition-colors hover:bg-muted/50">
                    <TableCell className="font-medium text-base">
                      {resourceType.resourceTypeNumber}
                    </TableCell>
                    <TableCell className="font-medium text-base">
                      <Link
                        to="/rema/resource-types/$resourceTypeNumber"
                        params={{ resourceTypeNumber: String(resourceType.resourceTypeNumber) }}
                        className="text-primary underline-offset-4 hover:underline"
                      >
                        {resourceType.resourceTypeName}
                      </Link>
                    </TableCell>
                    <TableCell className="text-base text-muted-foreground">
                      {resourceType.resourceTypeDescription || '-'}
                    </TableCell>
                    <TableCell>
                      <Badge className={CATEGORY_BADGE[resourceType.resourceTypeCategory] ?? ''}>
                        {resourceType.resourceTypeCategory}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-base text-muted-foreground">
                      {resourceType.parentResourceTypes.length > 0
                        ? getParentNames(resourceType.parentResourceTypes)
                        : '—'}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button asChild variant="ghost" size="icon" className="h-8 w-8">
                        <Link
                          to="/rema/resource-types/$resourceTypeNumber/edit"
                          params={{ resourceTypeNumber: String(resourceType.resourceTypeNumber) }}
                        >
                          <Edit className="h-4 w-4" />
                          <span className="sr-only">Edit {resourceType.resourceTypeName}</span>
                        </Link>
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={6} className="h-24 text-center text-muted-foreground">
                    No resource types found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>

          {totalPages > 1 && (
            <div className="flex items-center justify-between border-t py-3">
              <p className="text-base text-muted-foreground">
                Showing {startIndex + 1} to{" "}
                {Math.min(startIndex + itemsPerPage, filteredResourceTypes.length)} of{" "}
                {filteredResourceTypes.length} resource types
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="icon"
                  className="h-8 w-8"
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage((p) => p - 1)}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="text-sm font-medium">Page {currentPage} of {totalPages}</span>
                <Button
                  variant="outline"
                  size="icon"
                  className="h-8 w-8"
                  disabled={currentPage === totalPages}
                  onClick={() => setCurrentPage((p) => p + 1)}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
